// Webhook health (spec §6.3), backed by the Task-23 addition admin-webhooks
// (see that function's header for why it's read-only).
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/authContext';
import { callEdgeFunction } from '@/lib/api/edgeFunction';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/webhooks')({
  component: WebhooksPage,
});

interface WebhookEntry {
  id: string;
  processor: string;
  event_type: string | null;
  status: string;
  attempts: number;
  last_error: string | null;
  received_at: string;
  processed_at: string | null;
}

interface WebhookSummaryRow {
  event_type: string;
  last_received_at: string;
  succeeded: number;
  failed: number;
  processing: number;
}

interface WebhooksResponse {
  entries: WebhookEntry[];
  summary: WebhookSummaryRow[];
  stuck: WebhookEntry[];
}

function useWebhooks(status: string) {
  const { supabase } = useAuth();
  return useQuery({
    queryKey: ['admin-webhooks', status],
    queryFn: () => callEdgeFunction<WebhooksResponse>(supabase, 'admin-webhooks', { status: status || undefined, limit: 100 }),
  });
}

function WebhooksPage() {
  const [status, setStatus] = useState('');
  const { data, isLoading, error, refetch, isFetching } = useWebhooks(status);

  return (
    <section className="flex flex-col gap-lg">
      <div className="flex items-center justify-between gap-md">
        <div>
          <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Webhook health</h1>
          <p className="mt-sm text-body text-ink-2">Stripe/DPO event log — last-received per type, signature/processing failures.</p>
        </div>
        <button type="button" onClick={() => void refetch()} className="rounded-button border border-border px-md py-sm text-body text-ink">
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {isLoading ? <PageLoading /> : null}
      {error ? <p className="text-body text-danger">{(error as Error).message}</p> : null}

      {data && data.stuck.length > 0 ? (
        <div className="rounded-card border border-danger bg-tint-warning p-lg">
          <h2 className="text-h3 font-bold text-danger">Stuck processing ({data.stuck.length})</h2>
          <ul className="mt-sm flex flex-col gap-xs">
            {data.stuck.map((e) => (
              <li key={e.id} className="text-caption text-ink">
                {e.event_type ?? '(unknown)'} · {e.id} · received {new Date(e.received_at).toLocaleString()}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {data ? (
        <div className="overflow-x-auto rounded-card border border-border bg-surface shadow-card">
          <table className="w-full min-w-[640px] text-body text-ink">
            <thead>
              <tr className="border-b border-hair text-left text-caption text-ink-2">
                <th className="px-md py-sm font-semibold">Event type</th>
                <th className="px-md py-sm font-semibold">Last received</th>
                <th className="px-md py-sm font-semibold">Succeeded</th>
                <th className="px-md py-sm font-semibold">Failed</th>
                <th className="px-md py-sm font-semibold">Processing</th>
              </tr>
            </thead>
            <tbody>
              {data.summary.map((s) => (
                <tr key={s.event_type} className="border-b border-hair last:border-0">
                  <td className="px-md py-sm">{s.event_type}</td>
                  <td className="px-md py-sm">{new Date(s.last_received_at).toLocaleString()}</td>
                  <td className="px-md py-sm">{s.succeeded}</td>
                  <td className={`px-md py-sm ${s.failed > 0 ? 'font-semibold text-danger' : ''}`}>{s.failed}</td>
                  <td className="px-md py-sm">{s.processing}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div>
        <div className="flex gap-sm">
          {['', 'failed', 'processing', 'succeeded'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`rounded-button px-md py-xs text-caption font-semibold ${
                status === s ? 'bg-canyon text-white' : 'border border-border text-ink'
              }`}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
        {data ? (
          <ul className="mt-md flex flex-col gap-xs">
            {data.entries.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-sm border-b border-hair py-xs text-caption text-ink-2">
                <span>
                  {e.event_type ?? '(unknown)'} · {e.processor} · {e.id}
                </span>
                <span className={e.status === 'failed' ? 'font-semibold text-danger' : ''}>
                  {e.status} ({e.attempts}x) · {new Date(e.received_at).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
